import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import Login from "@/pages/Login";
import SetupPdg from "@/pages/SetupPdg";
import Activate from "@/pages/Activate";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Treasury from "@/pages/Treasury";
import Accounting from "@/pages/Accounting";
import HR from "@/pages/HR";
import Users from "@/pages/Users";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import { Suppliers, Clients, Purchases, Stock, Invoices } from "@/pages/operations";
import { Vehicles, Maintenance, FuelStock } from "@/pages/fleet";
import { POS, Chantiers } from "@/pages/business";
import { HSE, EPI, Legal, Mail, Workflows } from "@/pages/governance";
import AIAgents from "@/pages/AIAgents";
import Backup from "@/pages/Backup";
import OCRInvoice from "@/pages/OCRInvoice";
import "@/App.css";

// V3 modern SPA (Google Auth + lecture seule)
import "@/v3/v3.css";
import { V3AuthProvider } from "@/v3/auth";
import V3Login from "@/v3/Login";
import V3AuthCallback from "@/v3/AuthCallback";
import V3Layout from "@/v3/Layout";
import V3Dashboard from "@/v3/Dashboard";
import V3Journal from "@/v3/Journal";
import V3Caisse from "@/v3/Caisse";
import V3Sage from "@/v3/Sage";
import V3Employees from "@/v3/Employees";
import V3Expenses from "@/v3/Expenses";
import V3Groupe from "@/v3/Groupe";
import V3Livraisons from "@/v3/Livraisons";
import V3Devis from "@/v3/Devis";
import V3Audit from "@/v3/Audit";
import V3Status from "@/v3/Status";
import V3Factures from "@/v3/Factures";
import V3BankReconciliation from "@/v3/BankReconciliation";
import V3EntityDashboard from "@/v3/EntityDashboard";
import V3StorageHealth from "@/v3/StorageHealth";
import TwoFactorSetup from "@/v3/TwoFactorSetup";
import PWAInstaller from "@/v3/PWAInstaller";

// Redirige le root vers l'app legacy (HTML statique)
function RedirectToLegacy() {
  window.location.replace('/legacy-v2.html');
  return null;
}

// Détecte le fragment session_id (OAuth Emergent) en synchrone avant tout routing
function V3Router() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <V3AuthCallback />;
  }
  return (
    <Routes>
      <Route path="login" element={<V3Login />} />
      <Route element={<V3Layout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<V3Dashboard />} />
        <Route path="groupe" element={<V3Groupe />} />
        <Route path="journal" element={<V3Journal />} />
        <Route path="caisse" element={<V3Caisse />} />
        <Route path="sage" element={<V3Sage />} />
        <Route path="livraisons" element={<V3Livraisons />} />
        <Route path="devis" element={<V3Devis />} />
        <Route path="factures" element={<V3Factures />} />
        <Route path="bank-reconciliation" element={<V3BankReconciliation />} />
        <Route path="entity-dashboard" element={<V3EntityDashboard />} />
        <Route path="storage-health" element={<V3StorageHealth />} />
        <Route path="expenses" element={<V3Expenses />} />
        <Route path="employees" element={<V3Employees />} />
        <Route path="audit" element={<V3Audit />} />
      </Route>
      <Route path="*" element={<Navigate to="/v3/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <PWAInstaller />
      <Routes>
        {/* V3 modern SPA — wrap dans V3AuthProvider isolé */}
        <Route path="/v3/*" element={
          <V3AuthProvider>
            <V3Router />
          </V3AuthProvider>
        } />

        {/* Page Status publique (sans auth) — santé plateforme */}
        <Route path="/status" element={<V3Status />} />

        {/* Page 2FA Setup publique (CEO/DAF self-service) */}
        <Route path="/2fa-setup" element={<TwoFactorSetup />} />

        {/* Legacy redirect : / → legacy-v2.html */}
        <Route path="/" element={<RedirectToLegacy />} />

        {/* Anciennes routes React (abandonnées mais conservées pour compat) */}
        <Route path="/login" element={<AuthProvider><Login /></AuthProvider>} />
        <Route path="/setup" element={<AuthProvider><SetupPdg /></AuthProvider>} />
        <Route path="/activate/:token" element={<AuthProvider><Activate /></AuthProvider>} />
        <Route path="/app" element={<AuthProvider><AppLayout /></AuthProvider>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="treasury" element={<Treasury />} />
          <Route path="accounting" element={<Accounting />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="hr" element={<HR />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="clients" element={<Clients />} />
          <Route path="purchases" element={<Purchases />} />
          <Route path="stock" element={<Stock />} />
          <Route path="vehicles" element={<Vehicles />} />
          <Route path="maintenance" element={<Maintenance />} />
          <Route path="fuel" element={<FuelStock />} />
          <Route path="pos" element={<POS />} />
          <Route path="chantiers" element={<Chantiers />} />
          <Route path="hse" element={<HSE />} />
          <Route path="epi" element={<EPI />} />
          <Route path="legal" element={<Legal />} />
          <Route path="mail" element={<Mail />} />
          <Route path="workflows" element={<Workflows />} />
          <Route path="ai-agents" element={<AIAgents />} />
          <Route path="ocr" element={<OCRInvoice />} />
          <Route path="users" element={<Users />} />
          <Route path="audit" element={<Audit />} />
          <Route path="backup" element={<Backup />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        <Route path="*" element={<RedirectToLegacy />} />
      </Routes>
    </BrowserRouter>
  );
}
